import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import * as UTIF from 'utif';
import ImageWithZoom from './ImageWithZoom';

/**
 * FilePreviewRenderer Component
 * Renders preview for different file types (images, PDFs, TIFF files)
 * @param {File} file - The file to preview
 * @param {string} originalFileType - The MIME type of the file
 */
const FilePreviewRenderer = memo(({ file, originalFileType }) => {
  const canvasRef = useRef(null);
  const [isTiff, setIsTiff] = useState(false);
  const [tiffError, setTiffError] = useState(null);

  // Create object URL only once per file
  const objectUrl = useMemo(() => {
    if (file && file instanceof Blob) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file]);

  // Cleanup object URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  useEffect(() => {
    const renderTIFF = async () => {
      try {
        const buffer = await file.arrayBuffer();
        const ifds = UTIF.decode(buffer);
        if (!ifds.length) throw new Error('No IFDs found in TIFF');
        UTIF.decodeImage(buffer, ifds[0]);
        const rgba = UTIF.toRGBA8(ifds[0]);

        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = ifds[0].width;
        canvas.height = ifds[0].height;

        const ctx = canvas.getContext('2d');
        const imageData = new ImageData(
          new Uint8ClampedArray(rgba),
          ifds[0].width,
          ifds[0].height
        );
        ctx.putImageData(imageData, 0, 0);
      } catch (err) {
        console.error('TIFF render error:', err);
        setTiffError('Failed to render TIFF preview.');
      }
    };

    if (file && originalFileType) {
      const isTiffFile =
        originalFileType === 'image/tiff' ||
        file.name?.toLowerCase().endsWith('.tif') ||
        file.name?.toLowerCase().endsWith('.tiff');

      setIsTiff(isTiffFile);
      if (isTiffFile) renderTIFF();
    }
  }, [file, originalFileType]);

  if (!file || !(file instanceof Blob)) {
    return <p>Invalid or missing file.</p>;
  }

  if (isTiff) {
    return tiffError ? (
      <p>{tiffError}</p>
    ) : (
      <canvas
        ref={canvasRef}
        style={{ width: '100%', maxHeight: '295px', objectFit: 'contain' }}
      />
    );
  }

  if (originalFileType?.startsWith('image/')) {
    // return <ImageWithZoom src={objectUrl} alt="Preview" />;
    return (
      <img
        src={objectUrl}
        alt="Preview"
        className=""
        style={{ height: '100%', maxWidth: '100%', objectFit: 'contain' }}
        onLoad={() => URL.revokeObjectURL(objectUrl)}
      />
    );
  }

  if (originalFileType === 'application/pdf') {
    const objectUrl = URL.createObjectURL(file);
    return (
      <iframe
        src={objectUrl}
        title="PDF Preview"
        width="100%"
        height="495px"
        style={{ border: 'none' }}
        onLoad={() => URL.revokeObjectURL(objectUrl)}
      />
    );
  }

  return (
    <div className="text-center">
      <i className="bi bi-file-earmark-text fs-1" />
      <p className="mb-0">{file.name}</p>
      <small>({originalFileType || 'Unknown file type'})</small>
    </div>
  );
});

FilePreviewRenderer.displayName = 'FilePreviewRenderer';

export default FilePreviewRenderer;
